import { useQuery, UseQueryOptions } from "@tanstack/react-query";

import { request } from "@/lib/request";
import { Template, TemplateVisibility } from "@/lib/types";
import { useUser } from "../useUserProvider";

type UseGetTemplatesOptions = Omit<
  UseQueryOptions<Template[], Error>,
  "queryKey" | "queryFn"
> & {
  visibility?: TemplateVisibility;
  teamId?: string;
  categoryId?: string;
  subcategoryId?: string;
  q?: string;
  tags?: string[];
};

export const useGetTemplates = ({
  visibility,
  teamId,
  categoryId,
  subcategoryId,
  q,
  tags,
  enabled = true,
  ...options
}: UseGetTemplatesOptions = {}) => {
  const { accessToken } = useUser();

  return useQuery({
    ...options,
    queryKey: [
      "templates",
      {
        accessToken,
        visibility,
        teamId,
        categoryId,
        subcategoryId,
        q: q?.trim() || "",
        tags: tags?.join(",") || "",
      },
    ],
    enabled: !!accessToken && enabled,
    queryFn: async () => {
      if (!accessToken) {
        throw new Error("You need to be logged in to explore templates.");
      }

      const params: Record<string, string> = {};
      if (visibility) {
        params.visibility = visibility;
      }

      if (teamId) {
        params.teamId = teamId;
      }

      if (categoryId) {
        params.categoryId = categoryId;
      }

      if (subcategoryId) {
        params.subcategoryId = subcategoryId;
      }

      if (q?.trim()) {
        params.q = q.trim();
      }

      if (tags?.length) {
        params.tags = tags.join(",");
      }

      const res = await request
        .get<{ data: Template[] }>("/templates", { params })
        .catch(err => {
          throw new Error(
            err.response?.data.message
              ? err.response?.data.message
              : "Failed to fetch templates.",
          );
        });

      return res.data.data;
    },
  });
};
